import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight, Building2, ShieldCheck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GarageFlowLogo } from "@/components/Brand";
import { PublicHeader } from "@/components/PublicHeader";
import { LoadingState } from "@/components/common";
import { useAuth, homePathFor, customerMemberships } from "@/context/AuthContext";
import { t } from "@/i18n";

export function RequireAuth({ admin, children }) {
  const { me } = useAuth();
  const loc = useLocation();
  if (me === undefined) return <LoadingState full />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && !me.user.is_super_admin) return <Navigate to={homePathFor(me)} replace />;
  return children;
}

export function Landing() {
  const { me } = useAuth();
  const features = [
    { icon: Building2, title: t("landing.f1Title"), text: t("landing.f1Text") },
    { icon: Users, title: t("landing.f2Title"), text: t("landing.f2Text") },
    { icon: ShieldCheck, title: t("landing.f3Title"), text: t("landing.f3Text") },
  ];
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <section className="gf-grid-bg">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:py-28">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700 gf-rise">{t("landing.eyebrow")}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight text-[#0A2540] sm:text-5xl lg:text-6xl gf-rise">{t("landing.title")}</h1>
          <p className="mt-6 max-w-xl text-base text-slate-600 md:text-lg gf-rise">{t("landing.subtitle")}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="h-12 px-6" data-testid="landing-cta-button">
              <Link to={me ? homePathFor(me) : "/signup"}>{me ? t("landing.openApp") : t("p7.pricing.startTrial")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-6" data-testid="landing-pricing-button">
              <Link to="/pricing">{t("p7.pricing.nav")}</Link>
            </Button>
            {!me && <Button asChild size="lg" variant="ghost" className="h-12 px-6" data-testid="landing-signin-button"><Link to="/login">{t("landing.signIn")}</Link></Button>}
          </div>
        </div>
      </section>
      <section className="mx-auto grid max-w-6xl gap-6 px-5 py-16 md:grid-cols-3">
        {features.map(({ icon: Icon, title, text }, i) => (
          <div key={title} className="gf-card gf-rise p-8" style={{ animationDelay: `${i * 80}ms` }}>
            <Icon className="h-6 w-6 text-teal-600" />
            <p className="mt-4 font-display text-xl font-bold text-slate-900">{title}</p>
            <p className="mt-2 text-sm text-slate-600">{text}</p>
          </div>
        ))}
      </section>
      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-500">{t("brand.footer")}</footer>
    </div>
  );
}

export function NotFound({ inPortal }) {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-5 text-center" data-testid="not-found-page">
      {!inPortal && <GarageFlowLogo />}
      <p className="mt-10 font-display text-7xl font-extrabold text-[#0A2540]">404</p>
      <h1 className="mt-4 text-2xl font-bold text-slate-900">{inPortal ? t("portal.notFoundTitle") : t("notFound.title")}</h1>
      <p className="mt-2 max-w-md text-slate-600">{inPortal ? t("portal.notFoundText") : t("notFound.text")}</p>
      <Button asChild variant="outline" className="mt-8" data-testid="not-found-home-link"><Link to="/">{t("notFound.home")}</Link></Button>
    </div>
  );
}

export function AccountHub() {
  const { me, logout } = useAuth();
  const garages = customerMemberships(me.memberships);
  return (
    <div className="min-h-screen bg-slate-50 px-5 py-12">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center justify-between">
          <GarageFlowLogo />
          <Button variant="outline" onClick={logout} data-testid="account-hub-logout-button">{t("nav.logout")}</Button>
        </div>
        <h1 className="mt-10 text-3xl font-bold text-slate-900" data-testid="account-hub-title">{t("accountHub.title")}</h1>
        <p className="mt-2 text-slate-600">{t("accountHub.subtitle")}</p>
        <div className="mt-8 grid gap-4">
          {garages.length === 0 && <p className="gf-card p-6 text-sm text-slate-600" data-testid="account-hub-empty">{t("accountHub.empty")}</p>}
          <Link to="/signup" className="text-sm font-semibold text-teal-700 hover:text-teal-900" data-testid="account-hub-create-garage">{t("accountHub.createGarage")} →</Link>
          {garages.map((m) => (
            <Link key={m.garage_id} to={`/g/${m.slug}/account`} data-testid={`account-hub-garage-${m.slug}`} className="gf-card flex items-center gap-4 p-5 transition-transform duration-200 hover:-translate-y-0.5">
              <span className="h-10 w-2 rounded-full" style={{ background: m.primary_color }} />
              <span className="flex-1 font-display text-lg font-bold text-slate-900">{m.garage_name}</span>
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
